import { createContext, useContext } from 'react';
import type { DocumentData, Unsubscribe } from 'firebase/firestore';

// The context object and its hook live in a non-component module: a .tsx that
// exports both a component and a hook breaks fast refresh (react-refresh's
// only-export-components).
export interface FirebaseContextProps {
  updateDocument: (
    collectionName: string,
    docId: string,
    data: DocumentData
  ) => Promise<void>;
  addDocument: (
    collectionName: string,
    data: DocumentData,
    docId?: string
  ) => Promise<string>;
  deleteDocument: (collectionName: string, docId: string) => Promise<void>;
  // T is the caller's trusted doc shape (e.g. DrugSearch) — Firestore data is
  // untyped by nature, so the cast lives in the implementation, once.
  subscribeToDocument: <T = DocumentData>(
    collectionName: string,
    docId: string,
    callback: (data: T | null) => void,
    onError?: (error: Error) => void
  ) => Unsubscribe;
}

export const FirebaseContext = createContext<FirebaseContextProps>({
  updateDocument: async () => {},
  addDocument: async () => '',
  deleteDocument: async () => {},
  subscribeToDocument: () => () => {},
});

export const useFirebase = () => {
  return useContext(FirebaseContext);
};
